import { FC, useEffect } from "react";
import { CopaymentBillProps } from "../types/CopaymentBill.types";
import { useCopaymentBillInfo } from "../hooks/useCopamentBill";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import VerifiedIcon from "@mui/icons-material/Verified";
import SaveIcon from "@mui/icons-material/Save";
import { PaperBillTable } from "../../../PaperBilling/New/components/PaperBillTable";
import { DeleteConfirmationDialog } from "../../../DirectBilling/common/exports";
import { usePaperBillSummary } from "../../../PaperBilling/Edit/hooks/usePaperBillSummary";
import BillSummary from "../../../DirectBilling/NewBill/components/sub-components/BillSummary";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import { observer } from "mobx-react";
import useStore from "../../../../../../helpers/useStore";

const CopaymentBillInfo: FC<CopaymentBillProps> = observer((props) => {
  const {
    // state
    openDeleteDialog,
    tableVisible,
    isLoading,
    bills,
    notes,

    // handlers
    handleLoadAll,
    // handleAddSingleRx,
    setSelectedRxId,
    handleValidationFilter,
    handleSave,
    handleDeleteCancel,
    handleDeleteConfirm,
    setNotes,
    handleDeleteClick,

    // from props / from hook
    onRowClick,
    selectedRxId,
    anchorEl,
    handleMenuOpen,
    handleMenuClose,
  } = useCopaymentBillInfo(props);

  const { account, userName } = props;

  const { totalRxs, passedValidationRxs, problematicRxs, taxiertRxs, numberOfPayers } =
    usePaperBillSummary({ bills });
  const transactionStore = useStore(TransactionsContext);
  const { transactionRefresh } = transactionStore;

  useEffect(() => {
    handleLoadAll();
  }, [transactionRefresh]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Header Buttons */}
      <Box
        sx={{
          padding: 1,
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: "#5c758a",
          borderRadius: "0px 0px 0px 0px",
        }}
      >
        <Grid
          container
          spacing={1}
          alignItems="center"
          justifyContent={"space-evenly"}
        >
          <Grid
            item
            xs={0}
            className="header-button-container"
          >
            <Button
              className="button"
              endIcon={<FileDownloadIcon />}
              onClick={handleLoadAll}
            >
              Alle laden
            </Button>
          </Grid>

          {tableVisible && bills.length > 0 && (
            <>
              <Grid
                item
                xs={0}
                className="header-button-container"
              >
                <Button
                  className="button"
                  endIcon={<VerifiedIcon />}
                  onClick={handleValidationFilter}
                >
                  nur Valide
                </Button>
              </Grid>
              <Grid
                item
                xs={0}
                className="header-button-container"
              >
                <Button
                  className="button"
                  endIcon={<SaveIcon />}
                  onClick={handleSave}
                >
                  Speichern
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      {/* Loading */}
      {isLoading && (
        <Box className="loading-container">
          <CircularProgress color="primary" />
          <Chip
            label="Daten werden geladen, bitte warte..."
            className="loading-chip"
          />
        </Box>
      )}

      {/* If table is visible but no bills */}
      {tableVisible && !isLoading && bills.length === 0 && (
        <>
          <h2>Es wurden keine Verordnungen gefunden, für die eine Mahnung ausgestellt wurde.</h2>
          <p>{`Kostenstelle:  ${account}`}</p>
        </>
      )}

      {/* If table is visible and we have bills */}
      {tableVisible && bills.length > 0 && (
        <>
          <Box
            sx={{
              padding: 1,
              position: "sticky",
              top: 45,
              zIndex: 1000,
              backgroundColor: "#5c758a",
              borderRadius: "0px 0px 10px 10px",
            }}
          >
            <BillSummary
              totalRxs={totalRxs}
              passedValidationRxs={passedValidationRxs}
              problematicRxs={problematicRxs}
              taxiertRxs={taxiertRxs}
              numberOfPayers={numberOfPayers}
            />
          </Box>
          {bills.map((bill, index) => {
            const { payer, totalPrice } = bill;
            // const { payerDto, totalPrice } = data;
            const totalPriceFormatted = totalPrice.toFixed(2).replace(".", ",");
            const todayString = new Date().toDateString();

            return (
              <Box
                className="bill-card"
                key={index}
              >
                <Box className="bill-card-header">
                  <Box className="bill-card-body">
                    <Typography
                      variant="h6"
                      textAlign="center"
                    >
                      {payer?.name}
                    </Typography>
                  </Box>
                  <Box sx={{ m: 2 }}>
                    <Grid
                      container
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <Grid
                        item
                        xs={3}
                      >
                        <Typography
                          variant="subtitle1"
                          align="center"
                        >
                          Mandant
                        </Typography>
                        <Box className="box">
                          <Typography variant="subtitle2">{account}</Typography>
                        </Box>
                      </Grid>

                      <Grid
                        item
                        xs={3}
                      >
                        <Typography
                          variant="subtitle1"
                          align="center"
                        >
                          Startdatum
                        </Typography>
                        <Box className="box">
                          <Typography variant="subtitle2">{todayString}</Typography>
                        </Box>
                      </Grid>

                      <Grid
                        item
                        xs={3}
                      >
                        <Typography
                          variant="subtitle1"
                          align="center"
                        >
                          Erstellt von
                        </Typography>
                        <Box className="box">
                          <Typography variant="subtitle2">{userName}</Typography>
                        </Box>
                      </Grid>

                      <Grid
                        item
                        xs={3}
                      >
                        <Typography
                          variant="subtitle1"
                          align="center"
                        >
                          Gesamtpreis
                        </Typography>
                        <Box className="box">
                          <Typography variant="subtitle2">{totalPriceFormatted} €</Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Now we call DirectBillTable to show the Rxs in a table */}
                    <PaperBillTable
                      bill={bill}
                      selectedRxId={selectedRxId}
                      onRowClick={(row) => {
                        onRowClick(row);
                        setSelectedRxId(row.id);
                      }}
                      onMenuOpen={handleMenuOpen}
                    />
                    <Box className="notes-section">
                      <TextField
                        sx={{ mr: 1, mb: 1 }}
                        className="notes-textfield"
                        placeholder="Notizen hinzufügen..."
                        fullWidth
                        value={notes[bill.payer.ik] || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          setNotes((prev) => ({
                            ...prev,
                            [payer.ik]: val,
                          }));
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleDeleteClick}>Entfernen</MenuItem>
      </Menu>
    </Box>
  );
});

export default CopaymentBillInfo;
